// Marketplace verification — platform operators review SUBMITTED workforce-framework
// template listings and either verify (publish to the marketplace) or suspend them.
// A verified listing becomes installable by any tenant; the framework content was
// frozen at publish, so this is a content/trust review, not a code review.
import React, { useEffect, useState } from 'react';
import { Table, Button, Tag, message, Popconfirm, Space, Typography, Empty } from 'antd';
import { CheckCircleOutlined, StopOutlined, ReloadOutlined } from '@ant-design/icons';
import {
  getPendingListings, verifyListing, suspendListing, type MarketplaceListing,
} from '../../services/marketplaceService';

const { Title, Paragraph } = Typography;

const priceLabel = (l: MarketplaceListing): string => {
  if (!l.license_type || l.license_type === 'free') return 'Free';
  if (l.price_amount == null) return l.license_type;
  const amt = `${l.price_currency || ''} ${l.price_amount}`.trim();
  return l.license_type === 'subscription' ? `${amt}/${l.billing_period || 'mo'}` : amt;
};

const MarketplaceReviewPage: React.FC = () => {
  const [rows, setRows] = useState<MarketplaceListing[]>([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      setRows(await getPendingListings());
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Could not load pending listings');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const onVerify = async (id: string) => {
    setActing(id);
    try {
      await verifyListing(id);
      message.success('Listing verified and published to the marketplace.');
      await load();
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Verify failed');
    } finally {
      setActing(null);
    }
  };

  const onSuspend = async (id: string) => {
    setActing(id);
    try {
      await suspendListing(id, 'Rejected during review');
      message.success('Listing suspended.');
      await load();
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Suspend failed');
    } finally {
      setActing(null);
    }
  };

  const columns = [
    {
      title: 'Template',
      key: 'title',
      render: (_: unknown, l: MarketplaceListing) => (
        <div>
          <div style={{ fontWeight: 600 }}>{l.title}</div>
          {l.summary && <div style={{ color: '#888', fontSize: 12 }}>{l.summary}</div>}
        </div>
      ),
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      render: (c: string) => (c ? <Tag>{c}</Tag> : '—'),
    },
    {
      title: 'License',
      key: 'license',
      render: (_: unknown, l: MarketplaceListing) => (
        <Tag color={priceLabel(l) === 'Free' ? 'green' : 'gold'}>{priceLabel(l)}</Tag>
      ),
    },
    {
      title: 'Owner tenant',
      dataIndex: 'owner_tenant_id',
      key: 'owner',
      render: (id: string) => <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{id.slice(0, 8)}…</span>,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: unknown, l: MarketplaceListing) => (
        <Space>
          <Popconfirm
            title="Verify and publish this template to the marketplace?"
            onConfirm={() => onVerify(l.id)}
            okText="Verify"
          >
            <Button type="primary" size="small" icon={<CheckCircleOutlined />} loading={acting === l.id}>
              Verify
            </Button>
          </Popconfirm>
          <Popconfirm title="Suspend (reject) this listing?" onConfirm={() => onSuspend(l.id)} okText="Suspend" okButtonProps={{ danger: true }}>
            <Button danger size="small" icon={<StopOutlined />} loading={acting === l.id}>
              Reject
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <Title level={3} style={{ marginBottom: 0 }}>Template Marketplace — Verification</Title>
          <Paragraph type="secondary">
            Review workforce framework templates submitted by tenants. Verify to publish them to the
            marketplace (installable by any company), or reject to suspend.
          </Paragraph>
        </div>
        <Button icon={<ReloadOutlined />} onClick={load}>Refresh</Button>
      </div>
      <Table
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={rows}
        locale={{ emptyText: <Empty description="No templates awaiting verification" /> }}
        pagination={false}
      />
    </div>
  );
};

export default MarketplaceReviewPage;
